import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export interface CategoriaDeDado {
  categoria: string;
  descricao: string;
  registros: number;
  baseLegal: string;
  podeSerEliminado: boolean;
  retencao?: string;
}

export interface SumarioTitular {
  titular: { id: string; nome: string; anonimizado: boolean };
  dados: CategoriaDeDado[];
}

export interface PedidoLgpd {
  id: string;
  type: string;
  subjectLabel: string;
  status: 'open' | 'completed' | 'rejected';
  dueAt: string;
  completedAt: string | null;
  notes: string | null;
  createdAt: string;
  /** Negativo é atraso — e atraso em pedido de titular é o que vira sanção. */
  diasRestantes: number | null;
}

export interface ResultadoAnonimizacao {
  anonimizado: boolean;
  comprovante: string;
  documentosPurgados: number;
  usuarioDesativado: boolean;
  preservado: string;
}

const KEY = {
  sumario: (driverId: string) => ['lgpd', 'sumario', driverId] as const,
  pedidos: ['lgpd', 'pedidos'] as const,
};

export function useSumarioTitular(driverId: string | null) {
  return useQuery({
    queryKey: KEY.sumario(driverId ?? ''),
    enabled: Boolean(driverId),
    queryFn: async () => {
      const { data } = await api.get<{ data: SumarioTitular }>('/lgpd/data-summary', {
        params: { driverId },
      });
      return data.data;
    },
  });
}

export function usePedidosLgpd() {
  return useQuery({
    queryKey: KEY.pedidos,
    queryFn: async () => {
      const { data } = await api.get<{ data: PedidoLgpd[] }>('/lgpd/requests');
      return data.data;
    },
  });
}

/**
 * Baixa a exportação como arquivo.
 *
 * O titular tem direito ao dado "em formato de uso comum" (Art. 18, V) — um
 * JSON que ele possa abrir e levar para outro sistema, não uma tela de onde
 * copiar à mão.
 */
export function useExportarDados() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (driverId: string) => {
      const { data } = await api.post<{ data: Record<string, unknown> }>('/lgpd/export', {
        driverId,
      });

      const nome =
        (data.data.titular as { nome?: string } | undefined)?.nome ?? 'titular';
      const arquivo = new Blob([JSON.stringify(data.data, null, 2)], {
        type: 'application/json',
      });

      const url = URL.createObjectURL(arquivo);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dados-${nome.replace(/\W+/g, '-').toLowerCase()}-${
        new Date().toISOString().slice(0, 10)
      }.json`;
      link.click();
      URL.revokeObjectURL(url);

      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.pedidos }),
  });
}

export function useAnonimizar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { driverId: string; motivo?: string }) => {
      const { data } = await api.post<{ data: ResultadoAnonimizacao }>(
        '/lgpd/anonymize',
        input,
      );
      return data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lgpd'] });
      // O motorista some das listagens: foi apagado logicamente.
      void qc.invalidateQueries({ queryKey: ['drivers'] });
    },
  });
}

export function useAbrirPedido() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      type: string;
      subjectLabel: string;
      driverId?: string;
      notes?: string;
    }) => {
      const { data } = await api.post<{ data: PedidoLgpd }>('/lgpd/requests', input);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.pedidos }),
  });
}

export function useConcluirPedido() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; notes?: string }) => {
      const { data } = await api.post<{ data: { concluido: boolean } }>(
        `/lgpd/requests/${input.id}/complete`,
        { notes: input.notes },
      );
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.pedidos }),
  });
}

export const TIPO_PEDIDO_LABEL: Record<string, string> = {
  confirmation: 'Confirmação',
  access: 'Acesso',
  portability: 'Portabilidade',
  anonymization: 'Anonimização',
  opposition: 'Oposição',
  revocation: 'Revogação de consentimento',
};
